import React, {
  useState,
  Dispatch,
  SetStateAction,
  useCallback,
  useMemo,
  useRef,
  useEffect,
} from 'react'

function usePrevious<T>(value: T): T {
  const ref = useRef<T>(value)

  useEffect(() => {
    ref.current = value
  }, [value])

  return ref.current
}

export function useStoredState<T>(
  localStorageKey: string,
  initialState: T | (() => T),
): [T, Dispatch<SetStateAction<T>>] {
  const previousKey = usePrevious(localStorageKey)
  if (previousKey !== localStorageKey) {
    throw new Error("You shouldn't change the localStorageKey")
  }

  const initialLocalStorageData = useMemo<T | undefined>(() => {
    try {
      const stored = localStorage.getItem(localStorageKey)
      if (stored) {
        return JSON.parse(stored)
      }
    } catch {}
    return undefined
  }, [])

  const [state, setState] = useState(initialLocalStorageData ?? initialState)

  const setAndStoreState = useCallback<Dispatch<SetStateAction<T>>>(
    (newState) => {
      setTimeout(() => {
        localStorage.setItem(localStorageKey, JSON.stringify(newState))
      })
      setState(newState)
    },
    [localStorageKey, setState],
  )

  return [state, setAndStoreState]
}
